/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// v77.99.62 — Onboarding choreography. Three-step opinionated wizard
// that runs once for new households:
//
//   1. Add your child           (mandatory — every other page needs this)
//   2. Invite your co-parent    (optional — clean skip + placeholder co-parent helper)
//   3. Pick a parenting pattern (optional — opinionated defaults, can be set later)
//
// Design principles
//  • Every step has a friendly "Skip this step" affordance. We never
//    trap users in the wizard. If they skip we still write
//    `onboarding_completed`/`onboarding_dismissed` correctly so the
//    Dashboard cards then surface the right "what's next" nudges.
//  • Step 3 falls back gracefully when no co-parent has joined or
//    been placeheld — the user picks the pattern, we store
//    `parenting_schedule_status: "later"` and the Dashboard
//    onboarding checklist shows the remaining step.
//  • Inline help text uses sentence-case, no em-dashes, and matches
//    the calm/neutral brand voice.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ChevronLeft, Check, Users, Mail, Repeat, ArrowRight,
  CalendarDays, Sun, Coffee, Heart, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const TOTAL_STEPS = 3;

// Format today's local date as YYYY-MM-DD for the date input.
const todayYMD = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

// Opinionated pattern catalogue. Surfaces only the four canonical
// shapes plus the explicit "no shared parenting time" choice for
// sole-parent households. Everything else (custom weekly, fine
// handover times) is unlocked once the user is past the onboarding
// wizard and visits Calendar.
//
// Note the distinction we deliberately preserve:
//   - "later"          → undecided. User will pick a real pattern soon.
//   - "none_currently" → actively chose sole-parent / no shared time.
// Both write through `POST /api/custody/status` with the latter; the
// labels and toasts are tailored so the user sees their choice
// reflected back accurately on the Dashboard.
const PATTERNS = [
  {
    key: "alternating_weeks",
    label: "Alternating weeks",
    hint: "One full week with each parent. Simple and predictable.",
    icon: Repeat,
  },
  {
    key: "two_two_three",
    label: "2 / 2 / 3",
    hint: "Two days with you, two with the co-parent, three back with you. Repeats.",
    icon: CalendarDays,
  },
  {
    key: "every_second_weekend",
    label: "Every second weekend",
    hint: "Weekdays with one parent, alternating weekends with the other.",
    icon: Sun,
  },
  {
    key: "none_currently",
    label: "No current shared parenting time",
    hint: "Sole-parent right now, parallel parenting, or no shared custody yet. We'll skip every prompt to set one up.",
    icon: Heart,
  },
  {
    key: "later",
    label: "I'll set this up later",
    hint: "No problem. You can pick a pattern any time from the Calendar page.",
    icon: Coffee,
  },
];

const ProgressBar = ({ step }) => (
  <div className="w-full" data-testid="onboarding-progress">
    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-[#6F828A] mb-1.5">
      <span>Step {step} of {TOTAL_STEPS}</span>
      <span>{Math.round((step / TOTAL_STEPS) * 100)}%</span>
    </div>
    <div className="h-1.5 bg-[#EEF3F4] rounded-full overflow-hidden">
      <div
        className="h-full bg-[#1F9FA4] transition-all duration-300"
        style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
        data-testid="onboarding-progress-bar"
      />
    </div>
  </div>
);

const SkipLink = ({ onClick, label = "Skip for now", testid = "onboarding-skip" }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-sm text-[#6F828A] hover:text-[#2E3A3F] underline-offset-2 hover:underline transition-colors"
    data-testid={testid}
  >
    {label}
  </button>
);

export default function OnboardingFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // ----- Step 1 state — Add child
  const [child, setChild] = useState({ name: "", dob: "", medical: "" });
  const [createdChildId, setCreatedChildId] = useState(null);

  // ----- Step 2 state — Invite co-parent
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const [placeholderName, setPlaceholderName] = useState("");
  const [placeholderSaved, setPlaceholderSaved] = useState(false);

  // ----- Step 3 state — Parenting pattern
  const [pattern, setPattern] = useState("alternating_weeks");

  // Don't trap users who've already completed onboarding (or who've
  // organically satisfied the criteria). Bounce them to /dashboard so
  // the route never feels sticky.
  //
  // v77.99.68 — Mediators / third-party professionals must never see
  // the parent setup wizard at all. They land here only when a stale
  // sessionStorage flag fires the one-shot redirect — bail
  // immediately so they go to their read-only dashboard instead.
  useEffect(() => {
    if (user?.role === "mediator") {
      navigate("/app/dashboard", { replace: true });
      return undefined;
    }
    let cancelled = false;
    api.get("/onboarding/status").then((r) => {
      if (cancelled) return;
      if (r.data?.completed || (!r.data?.show_full_onboarding && !r.data?.show_checklist)) {
        navigate("/app/dashboard", { replace: true });
      }
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[onboarding] status load failed:", err?.response?.status || err?.message);
    });
    return () => { cancelled = true; };
  }, [navigate, user?.role]);

  const goNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const skipFlow = async () => {
    try { await api.post("/onboarding/skip"); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[onboarding] skip failed:", err?.response?.status || err?.message);
    }
    navigate("/app/dashboard", { replace: true });
  };

  const finishFlow = async () => {
    try { await api.post("/onboarding/complete"); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[onboarding] complete failed:", err?.response?.status || err?.message);
    }
    toast.success("You're all set. Welcome to your household.");
    navigate("/app/dashboard", { replace: true });
  };

  // ----- Step 1 submit
  const saveChild = async (e) => {
    e?.preventDefault?.();
    if (!child.name.trim()) {
      toast.error("Please enter your child's name.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/children", {
        name: child.name.trim(),
        dob: child.dob || null,
        medical_notes: child.medical || "",
      });
      // Different versions of the API surface the new id on different
      // keys; tolerate both shapes so the schedule step can use it.
      const newId = r.data?.id || r.data?.child?.id || r.data?.child_id || null;
      if (newId) setCreatedChildId(newId);
      toast.success("Child added");
      goNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save child");
    } finally {
      setBusy(false);
    }
  };

  // ----- Step 2 submit (email invite path)
  const sendInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteEmail.trim()) {
      toast.error("Enter an email to send the invite.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/household/invite/email", { email: inviteEmail.trim() });
      setInviteSent(true);
      toast.success("Invite sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally {
      setBusy(false);
    }
  };

  // ----- Step 2 — placeholder fallback. Users who DON'T have a real
  // co-parent email yet can still record a name so messages/expenses
  // can be attributed neutrally. Replaceable later when the co-parent
  // joins for real.
  const savePlaceholder = async (e) => {
    e?.preventDefault?.();
    if (!placeholderName.trim()) {
      toast.error("Please enter a name for the co-parent placeholder.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/household/placeholder-coparent", { name: placeholderName.trim() });
      setPlaceholderSaved(true);
      toast.success("Saved. You can invite the real account later from Settings.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save placeholder");
    } finally {
      setBusy(false);
    }
  };

  // ----- Step 3 submit — Parenting pattern.
  //
  // Three families of behaviour:
  //   • "later"          → store `parenting_schedule_status: "none_currently"`
  //                        as a "you can revisit any time" stub. Same writes
  //                        as Calendar's "I don't have a current parenting
  //                        schedule" link.
  //   • "none_currently" → identical write to "later" but the toast wording
  //                        confirms the deliberate sole-parent choice so the
  //                        user knows we've actually heard them.
  //   • any pattern      → PUT a real /custody/schedule when we can identify
  //                        a co-parent, otherwise store the choice as a
  //                        household preference for later setup.
  const savePattern = async () => {
    setBusy(true);
    try {
      if (pattern === "later" || pattern === "none_currently") {
        try { await api.post("/custody/status", { status: "none_currently" }); }
        catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[onboarding] custody status failed:", err?.response?.status || err?.message);
        }
        if (pattern === "none_currently") {
          // v77.99.63 — Sole-parent / no-shared-time households also
          // want their expense default to stop assuming reimbursement.
          // Flip the household default to 100/0 (payer keeps the
          // whole cost) so the Expenses page reads "tracking spend",
          // not "splitting with a co-parent". Best-effort; failures
          // are silent because the choice is recoverable from Settings.
          try {
            await api.put("/household/default-split", { split_type: "100_0" });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[onboarding] default-split 100_0 set failed:", err?.response?.status || err?.message);
          }
          toast.success("Saved. We'll skip every 'set up a schedule' prompt and only resurface it if you ask.");
        }
        await finishFlow();
        return;
      }
      // Try to read the household to identify parent_a/parent_b. If no
      // real co-parent + no placeholder, we can't build a complete
      // schedule yet, so we fall back to recording the user's
      // preferred pattern as a household preference and finish.
      const meRes = await api.get("/auth/me");
      const me = meRes.data;
      const memRes = await api.get("/household");
      const members = memRes.data?.members || [];
      const others = members.filter((m) => m.id !== me.id);
      const partner = others[0] || null;

      if (!partner) {
        // No partner identified — store the chosen pattern as a
        // household preference for later setup. Backend tolerates
        // unknown extra fields on this endpoint.
        try {
          await api.post("/household/preferences", { preferred_schedule_pattern: pattern });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[onboarding] preferences save failed:", err?.response?.status || err?.message);
        }
        toast.success("Pattern saved. Finish setting it up once your co-parent joins.");
        await finishFlow();
        return;
      }

      const body = {
        pattern,
        start_date: todayYMD(),
        parent_a_id: me.id,
        parent_b_id: partner.id,
        ...(createdChildId ? { child_id: createdChildId } : {}),
      };
      try {
        await api.put("/custody/schedule", body);
        toast.success("Schedule saved. You can refine handover times any time.");
      } catch (err) {
        // Non-fatal: don't trap the user in the wizard if the schedule
        // payload was rejected. We surface the error and still finish.
        // eslint-disable-next-line no-console
        console.warn("[onboarding] schedule save failed:", err?.response?.status || err?.message);
        toast.message("Saved your preference. Finish setting up the schedule from Calendar when you're ready.");
      }
      await finishFlow();
    } finally {
      setBusy(false);
    }
  };

  const StepIcon = useMemo(() => ({
    1: Users,
    2: Mail,
    3: Repeat,
  }[step] || Sparkles), [step]);

  return (
    <div className="min-h-screen bg-[#F5F7F8] flex flex-col" data-testid="onboarding-page" data-step={step}>
      <header className="bg-white border-b border-[#D9E1E3] px-4 sm:px-6 py-3">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          {step > 1 ? (
            <button
              type="button"
              onClick={goBack}
              className="text-[#6F828A] hover:text-[#2E3A3F] inline-flex items-center gap-1 text-sm"
              data-testid="onboarding-back"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          ) : <div className="w-10" />}
          <div className="flex-1 text-center">
            <span className="font-heading text-base font-semibold text-[#2E3A3F]">CoParent Console</span>
          </div>
          <SkipLink onClick={skipFlow} />
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-10">
        <div className="max-w-xl mx-auto space-y-6">
          <ProgressBar step={step} />

          {/* Step header */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0">
              <StepIcon className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              {step === 1 && (
                <>
                  <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#2E3A3F]" data-testid="onboarding-step-title">Add your child</h1>
                  <p className="text-[#6F828A] mt-1 text-sm sm:text-base">{user?.name ? `Welcome, ${user.name.split(" ")[0]}. ` : "Welcome. "}Let's set up your household in three quick steps. Start with the basics for your child.</p>
                </>
              )}
              {step === 2 && (
                <>
                  <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#2E3A3F]" data-testid="onboarding-step-title">Invite your co-parent</h1>
                  <p className="text-[#6F828A] mt-1 text-sm sm:text-base">Share your household so you can both manage things together. You can do this later.</p>
                </>
              )}
              {step === 3 && (
                <>
                  <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#2E3A3F]" data-testid="onboarding-step-title">Pick a parenting pattern</h1>
                  <p className="text-[#6F828A] mt-1 text-sm sm:text-base">Pick the shape that's closest to your arrangement. You can fine-tune handover times later from the Calendar page.</p>
                </>
              )}
            </div>
          </div>

          {/* Step body */}
          <div className="bg-white border border-[#D9E1E3] rounded-md p-5 sm:p-6 space-y-4">

            {/* ----- Step 1: Add child ----- */}
            {step === 1 && (
              <form onSubmit={saveChild} className="space-y-4" data-testid="onboarding-step-1">
                <div>
                  <Label htmlFor="ob-child-name">Child name <span className="text-[#C0291D]">*</span></Label>
                  <Input
                    id="ob-child-name"
                    value={child.name}
                    onChange={(e) => setChild({ ...child, name: e.target.value })}
                    placeholder="e.g. Mia"
                    required
                    data-testid="onboarding-child-name"
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="ob-child-dob">Date of birth <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                  <Input
                    id="ob-child-dob"
                    type="date"
                    value={child.dob}
                    onChange={(e) => setChild({ ...child, dob: e.target.value })}
                    data-testid="onboarding-child-dob"
                  />
                </div>
                <div>
                  <Label htmlFor="ob-child-medical">Medical info <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                  <Textarea
                    id="ob-child-medical"
                    value={child.medical}
                    onChange={(e) => setChild({ ...child, medical: e.target.value })}
                    placeholder="Allergies, conditions, medications…"
                    rows={2}
                    data-testid="onboarding-child-medical"
                  />
                </div>
                <p className="text-[12px] text-[#6F828A]">Private by default. Only people you invite to this household can see it.</p>
              </form>
            )}

            {/* ----- Step 2: Invite co-parent ----- */}
            {step === 2 && (
              <div className="space-y-5" data-testid="onboarding-step-2">
                <form onSubmit={sendInvite} className="space-y-3">
                  <div>
                    <Label htmlFor="ob-invite-email">Co-parent's email</Label>
                    <Input
                      id="ob-invite-email"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="coparent@example.com"
                      disabled={inviteSent}
                      data-testid="onboarding-invite-email"
                    />
                  </div>
                  {inviteSent && (
                    <div className="flex items-start gap-2 text-sm text-[#116B6E] bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm p-3" data-testid="onboarding-invite-sent">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Invite sent to <strong>{inviteEmail}</strong>. They'll receive a link with your invite code.</span>
                    </div>
                  )}
                </form>

                {/* Placeholder fallback. Quietly tucked below so we don't
                    push the primary path. Only renders when no email
                    has been entered yet. */}
                {!inviteSent && (
                  <details className="border-t border-[#EEF3F4] pt-4" data-testid="onboarding-placeholder-section">
                    <summary className="text-sm text-[#4A6170] cursor-pointer hover:text-[#2E3A3F]">
                      Don't have their email yet?
                    </summary>
                    <form onSubmit={savePlaceholder} className="mt-3 space-y-3">
                      <p className="text-[12.5px] text-[#6F828A] leading-relaxed">
                        Add a placeholder for your co-parent so messages, expenses and the schedule
                        can be attributed neutrally. You can swap it for the real account from
                        Settings whenever they're ready to join.
                      </p>
                      <div>
                        <Label htmlFor="ob-placeholder-name">Co-parent name</Label>
                        <Input
                          id="ob-placeholder-name"
                          value={placeholderName}
                          onChange={(e) => setPlaceholderName(e.target.value)}
                          placeholder="e.g. Sam"
                          disabled={placeholderSaved}
                          data-testid="onboarding-placeholder-name"
                        />
                      </div>
                      {placeholderSaved ? (
                        <div className="flex items-start gap-2 text-sm text-[#116B6E] bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm p-3" data-testid="onboarding-placeholder-saved">
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Placeholder saved. You can invite the real account any time.</span>
                        </div>
                      ) : (
                        <Button
                          type="submit"
                          disabled={busy || !placeholderName.trim()}
                          variant="outline"
                          className="border-[#A7D8DA] text-[#116B6E] hover:bg-[#E0F2F2]"
                          data-testid="onboarding-placeholder-save"
                        >
                          {busy ? "Saving…" : "Add placeholder co-parent"}
                        </Button>
                      )}
                    </form>
                  </details>
                )}
              </div>
            )}

            {/* ----- Step 3: Parenting pattern ----- */}
            {step === 3 && (
              <div className="space-y-3" data-testid="onboarding-step-3" role="radiogroup" aria-label="Parenting pattern">
                {PATTERNS.map((p) => {
                  const Icon = p.icon;
                  const active = pattern === p.key;
                  return (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => setPattern(p.key)}
                      aria-pressed={active}
                      role="radio"
                      aria-checked={active}
                      className={`w-full text-left flex items-start gap-3 p-4 border rounded-sm transition-colors ${
                        active
                          ? "bg-[#E8F0F1] border-[#1F9FA4] ring-1 ring-[#1F9FA4]/40"
                          : "bg-white border-[#D9E1E3] hover:bg-[#F5F7F8]"
                      }`}
                      data-testid={`onboarding-pattern-${p.key}`}
                      data-active={active ? "true" : "false"}
                    >
                      <div className={`w-9 h-9 rounded-sm inline-flex items-center justify-center flex-shrink-0 ${active ? "bg-[#1F9FA4] text-white" : "bg-[#EEF3F4] text-[#4A6170]"}`}>
                        <Icon className="w-4 h-4" strokeWidth={1.7} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#2E3A3F]">{p.label}</div>
                        <div className="text-[12.5px] text-[#6F828A] mt-0.5 leading-relaxed">{p.hint}</div>
                      </div>
                      {active && <Check className="w-4 h-4 text-[#116B6E] mt-1 flex-shrink-0" strokeWidth={2.2} />}
                    </button>
                  );
                })}
                {/* v77.99.64 — Transparent disclosure of the side-effect
                    we're about to apply when the user picks the sole-
                    parent path. Without this note the wizard would
                    silently flip the household expense default to
                    100/0, which is the right default but a surprise
                    if not explained. The user can change it any time
                    from Settings, with or without a co-parent joining. */}
                {pattern === "none_currently" && (
                  <div
                    className="text-[12.5px] text-[#6F4E1E] bg-[#FBF5E8] border border-[#EADFC9] rounded-sm p-3 leading-relaxed"
                    data-testid="onboarding-sole-care-note"
                  >
                    <strong className="font-medium">Heads up:</strong> we'll also default new expenses to <strong className="font-medium">100 / 0</strong> (you keep the whole cost) since there's no shared parenting time. You can change this any time from Settings, including if a co-parent or placeholder joins later.
                  </div>
                )}
                <p className="text-[12px] text-[#6F828A]">You can change this any time. Pattern choice never deletes existing events.</p>
              </div>
            )}
          </div>

          {/* Footer CTAs */}
          <div className="flex flex-col gap-2">
            {step === 1 && (
              <Button
                onClick={saveChild}
                disabled={busy || !child.name.trim()}
                className="bg-[#1F9FA4] hover:bg-[#178085] w-full h-12 text-base"
                data-testid="onboarding-cta-primary"
              >
                {busy ? "Saving…" : "Save and continue"}
              </Button>
            )}
            {step === 2 && (
              <>
                {!inviteSent && !placeholderSaved ? (
                  <Button
                    onClick={sendInvite}
                    disabled={busy || !inviteEmail.trim()}
                    className="bg-[#1F9FA4] hover:bg-[#178085] w-full h-12 text-base"
                    data-testid="onboarding-cta-primary"
                  >
                    {busy ? "Sending…" : "Send invite"}
                  </Button>
                ) : (
                  <Button
                    onClick={goNext}
                    className="bg-[#1F9FA4] hover:bg-[#178085] w-full h-12 text-base"
                    data-testid="onboarding-cta-primary"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={goNext}
                  className="text-[#6F828A] w-full h-11"
                  data-testid="onboarding-step2-skip"
                >
                  Skip this step
                </Button>
              </>
            )}
            {step === 3 && (
              <Button
                onClick={savePattern}
                disabled={busy}
                className="bg-[#1F9FA4] hover:bg-[#178085] w-full h-12 text-base"
                data-testid="onboarding-cta-primary"
              >
                {busy
                  ? "Saving…"
                  : pattern === "later"
                    ? "Finish setup"
                    : pattern === "none_currently"
                      ? "Confirm and finish"
                      : "Save pattern and finish"}
              </Button>
            )}
          </div>

          <div className="text-center pt-2">
            <SkipLink onClick={skipFlow} testid="onboarding-skip-bottom" />
          </div>
        </div>
      </main>
    </div>
  );
}
